import {
    ArrowDownIcon,
    ArrowUpIcon,
    CheckCircleIcon,
    PencilIcon,
    PlusIcon,
    TagIcon,
    TrashIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useAdmin } from "../../contexts/AdminContext";
import { adminService } from "../../services/api";

const CategoryManagement = () => {
    const { refreshCategories, refreshKey } = useAdmin();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [usageData, setUsageData] = useState({});
    const [reordering, setReordering] = useState(false);

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const response = await adminService.getCategories();

            if (response.status === "success") {
                setCategories(response.data.categories);

                const usagePromises = response.data.categories.map(cat =>
                    adminService.getCategoryUsage(cat._id).catch(() => ({ data: { usage: { total: 0 } } }))
                );
                const usageResults = await Promise.all(usagePromises);
                const usageMap = {};
                response.data.categories.forEach((cat, index) => {
                    usageMap[cat._id] = usageResults[index]?.data?.usage || { products: 0, designs: 0, templates: 0, total: 0 };
                });
                setUsageData(usageMap);
            }
        } catch (error) {
            console.error("Failed to fetch categories:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories, refreshKey]);

    const handleDeleteCategory = async (categoryId) => {
        const usage = usageData[categoryId];
        if (usage && usage.total > 0) {
            alert(`Cannot delete category. It is being used by ${usage.products} product(s), ${usage.designs} design(s), and ${usage.templates} template(s).`);
            return;
        }

        if (window.confirm("Are you sure you want to delete this category?")) {
            try {
                await adminService.deleteCategory(categoryId);
                await refreshCategories();
                fetchCategories();
            } catch (error) {
                console.error("Failed to delete category:", error);
                alert(error.response?.data?.message || "Failed to delete category");
            }
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setShowModal(true);
    };

    const handleCreateCategory = () => {
        setEditingCategory(null);
        setShowModal(true);
    };

    const handleReorder = async (categoryId, direction) => {
        const currentIndex = categories.findIndex(cat => cat._id === categoryId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= categories.length) return;

        const newCategories = [...categories];
        [newCategories[currentIndex], newCategories[newIndex]] = [newCategories[newIndex], newCategories[currentIndex]];

        setCategories(newCategories);

        try {
            setReordering(true);
            const order = newCategories.map(cat => cat._id);
            await adminService.reorderCategories(order);
        } catch (error) {
            console.error("Failed to reorder categories:", error);
            alert(error.response?.data?.message || "Failed to reorder categories");
            await refreshCategories();
            fetchCategories();
        } finally {
            setReordering(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Category Management
                    </h2>
                    <p className="text-gray-600 mt-1">Manage product, design, and template categories</p>
                </div>
                <button
                    onClick={handleCreateCategory}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl hover:from-[#008000] hover:to-emerald-700 transition-colors shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add Category</span>
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008000]"></div>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gradient-to-r from-green-50 to-emerald-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Usage</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {categories.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                            No categories found. Create your first category!
                                        </td>
                                    </tr>
                                ) : (
                                    categories.map((category, index) => {
                                        const usage = usageData[category._id] || { products: 0, designs: 0, templates: 0, total: 0 };
                                        return (
                                            <tr
                                                key={category._id}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col items-center space-y-1">
                                                        <span className="text-sm font-medium text-gray-600">
                                                            {index + 1}
                                                        </span>
                                                        <div className="flex flex-col space-y-1">
                                                            <button
                                                                onClick={() => handleReorder(category._id, 'up')}
                                                                disabled={index === 0 || reordering}
                                                                className={`p-1 rounded transition-colors ${index === 0 || reordering
                                                                    ? "text-gray-300 cursor-not-allowed"
                                                                    : "text-[#008000] hover:bg-green-50"
                                                                    }`}
                                                            >
                                                                <ArrowUpIcon className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleReorder(category._id, 'down')}
                                                                disabled={index === categories.length - 1 || reordering}
                                                                className={`p-1 rounded transition-colors ${index === categories.length - 1 || reordering
                                                                    ? "text-gray-300 cursor-not-allowed"
                                                                    : "text-[#008000] hover:bg-green-50"
                                                                    }`}
                                                            >
                                                                <ArrowDownIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {category.image?.url ? (
                                                        <img
                                                            src={category.image.url}
                                                            alt={category.name}
                                                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                                            <TagIcon className="w-8 h-8 text-gray-400" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-3">
                                                        <span className="font-medium text-gray-900 capitalize">
                                                            {category.name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {category.isActive ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-[#008000]">
                                                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                            <XCircleIcon className="w-4 h-4 mr-1" />
                                                            Inactive
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-600">
                                                        <div>Products: {usage.products}</div>
                                                        <div>Designs: {usage.designs}</div>
                                                        <div>Templates: {usage.templates}</div>
                                                        <div className="font-semibold text-gray-900 mt-1">
                                                            Total: {usage.total}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => handleEditCategory(category)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        >
                                                            <PencilIcon className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCategory(category._id)}
                                                            disabled={usage.total > 0}
                                                            className={`p-2 rounded-lg transition-colors ${usage.total > 0
                                                                ? "text-gray-400 cursor-not-allowed"
                                                                : "text-red-600 hover:bg-red-50"
                                                                }`}
                                                        >
                                                            <TrashIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showModal && (
                <CategoryModal
                    category={editingCategory}
                    onClose={() => setShowModal(false)}
                    onSave={async () => {
                        setShowModal(false);
                        await refreshCategories();
                        fetchCategories();
                    }}
                />
            )}
        </div>
    );
};

const CategoryModal = ({ category, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: category?.name || "",
        isActive: category?.isActive ?? true,
        subcategories: category?.subcategories || []
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(category?.image?.url || null);
    const [subcategoryImages, setSubcategoryImages] = useState({}); // { index: File }
    const [subcategoryPreviews, setSubcategoryPreviews] = useState({}); // { index: dataUrl }
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || "",
                isActive: category.isActive ?? true,
                subcategories: category.subcategories || []
            });
            setImagePreview(category.image?.url || null);
            setImageFile(null);
            setSubcategoryImages({});
            setSubcategoryPreviews({});
        } else {
            setFormData({
                name: "",
                isActive: true,
                subcategories: []
            });
            setImagePreview(null);
            setImageFile(null);
            setSubcategoryImages({});
            setSubcategoryPreviews({});
        }
    }, [category]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(category?.image?.url || null);
    };

    const handleAddSubcategory = () => {
        setFormData({
            ...formData,
            subcategories: [...formData.subcategories, { name: "", image: null }]
        });
    };

    const handleRemoveSubcategory = (index) => {
        const newSubs = [...formData.subcategories];
        newSubs.splice(index, 1);
        setFormData({ ...formData, subcategories: newSubs });

        // Clean up images for this index
        const newImages = { ...subcategoryImages };
        delete newImages[index];
        setSubcategoryImages(newImages);

        const newPreviews = { ...subcategoryPreviews };
        delete newPreviews[index];
        setSubcategoryPreviews(newPreviews);
    };

    const handleSubcategoryChange = (index, value) => {
        const newSubs = [...formData.subcategories];
        newSubs[index].name = value;
        setFormData({ ...formData, subcategories: newSubs });
    };

    const handleSubcategoryImageChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            setSubcategoryImages({ ...subcategoryImages, [index]: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setSubcategoryPreviews({ ...subcategoryPreviews, [index]: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);

        try {
            // Prepare subcategory data (sanitize to remove image urls if they are not needed by backend)
            const subcategoriesData = formData.subcategories.map((sub, index) => ({
                name: sub.name,
                image: sub.image // Keep existing image if not replaced
            }));

            // Build subcategory images map for API
            const subImagesMap = {};
            Object.keys(subcategoryImages).forEach(index => {
                subImagesMap[`subcategory_image_${index}`] = subcategoryImages[index];
            });

            if (category) {
                await adminService.updateCategory(
                    category._id,
                    {
                        name: formData.name,
                        isActive: formData.isActive,
                        subcategories: subcategoriesData
                    },
                    imageFile,
                    subImagesMap
                );
            } else {
                await adminService.createCategory(
                    formData.name,
                    formData.isActive,
                    imageFile,
                    subcategoriesData,
                    subImagesMap
                );
            }
            onSave();
        } catch (error) {
            console.error("Failed to save category:", error);
            setError(error.response?.data?.message || error.message || "Failed to save category");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border border-green-200 max-w-2xl shadow-2xl rounded-2xl bg-white/95 backdrop-blur-xl mb-10">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                            {category ? "Edit Category" : "Create Category"}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                            {category ? "Update category and subcategory information" : "Add a new category and its subcategories"}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Left Column: Basic Info */}
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                    placeholder="e.g., Visiting Cards"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">Converted to lowercase automatically</p>
                            </div>

                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    className="w-5 h-5 text-[#008000] border-gray-300 rounded focus:ring-green-500"
                                />
                                <label htmlFor="isActive" className="text-sm font-semibold text-gray-700">
                                    Active (Visibile to customers)
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category Image
                                </label>
                                <div className="space-y-3">
                                    {imagePreview ? (
                                        <div className="relative group">
                                            <img
                                                src={imagePreview}
                                                alt="Category preview"
                                                className="w-full h-40 object-cover rounded-2xl border border-gray-200 shadow-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleRemoveImage}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600 transition-colors"
                                            >
                                                <XMarkIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-full h-40 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center bg-gray-50 text-gray-400">
                                            <TagIcon className="h-10 w-10 mb-2" />
                                            <span className="text-sm">No image uploaded</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-[#008000] hover:file:bg-green-100 transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Subcategories */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <label className="block text-sm font-bold text-gray-800">
                                    Subcategories
                                </label>
                                <button
                                    type="button"
                                    onClick={handleAddSubcategory}
                                    className="text-xs flex items-center space-x-1 px-3 py-1.5 bg-green-50 text-[#008000] rounded-lg hover:bg-green-100 transition-colors font-semibold"
                                >
                                    <PlusIcon className="w-3.5 h-3.5" />
                                    <span>Add New</span>
                                </button>
                            </div>

                            <div className="max-h-[400px] overflow-y-auto pr-2 space-y-4 custom-scrollbar">
                                {formData.subcategories.length === 0 ? (
                                    <div className="text-center py-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 italic text-sm">
                                        No subcategories added yet
                                    </div>
                                ) : (
                                    formData.subcategories.map((sub, index) => (
                                        <div
                                            key={index}
                                            className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-green-200 transition-colors space-y-3"
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Subcategory #{index + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveSubcategory(index)}
                                                    className="p-1 text-red-400 hover:text-red-600 transition-colors"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="space-y-3">
                                                <input
                                                    type="text"
                                                    value={sub.name}
                                                    onChange={(e) => handleSubcategoryChange(index, e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50 outline-none transition-all"
                                                    placeholder="Subcategory Name"
                                                    required
                                                />

                                                <div className="flex items-center space-x-3">
                                                    <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex-shrink-0 overflow-hidden">
                                                        {(subcategoryPreviews[index] || sub.image?.url) ? (
                                                            <img
                                                                src={subcategoryPreviews[index] || sub.image?.url}
                                                                alt="Preview"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <TagIcon className="w-6 h-6 text-gray-300" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleSubcategoryImageChange(index, e)}
                                                            className="text-[10px] w-full file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-all cursor-pointer"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700 flex items-center space-x-2">
                            <span className="font-bold">Error:</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-8 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-10 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl hover:from-[#008000] hover:to-emerald-700 transition-all shadow-lg hover:shadow-green-500/25 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                        >
                            {saving ? "Saving Changes..." : category ? "Update Category" : "Create Category"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CategoryManagement;

